
import React, { lazy, Suspense, useEffect, useState } from 'react';

const LearningProgressChart = lazy(() => import('./LearningProgressChart'));

const HowTo = () => {
  const [openImage, setOpenImage] = useState(null);

  const apiBase = (process.env.REACT_APP_USER_API_URL || '').replace(/\/$/, '');

  const intonationImage = {
    src: `${apiBase}/uploads/screens/muchwarmerthere.png`,
    title: 'How to read the intonation curve',
    subtitle: 'Example: “Wouldn’t it be much warmer there?”',
  };

  const aiImage = {
    src: `${apiBase}/uploads/screens/response_ai.png`,
    title: 'AI feedback as a pointer',
    subtitle:
      'Use marked phonemes and words as suggestions for what to notice next.',
  };

  useEffect(() => {
    if (!openImage) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenImage(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [openImage]);

  return (
    <>
      <div
        id="howto"
        className="rounded-[34px] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/70 md:p-10"
      >
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex rounded-full border border-blue-100 bg-blue-50 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">
            How to practise
          </div>

          <h1 className="mt-5 text-3xl font-black tracking-tight text-slate-950 md:text-5xl">
            Practise with human-read model phrases.
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base font-semibold leading-8 text-slate-600 md:text-lg">
            Catchphraser practice is built around listening, repeating,
            comparing, and returning to useful speech patterns over time.
          </p>
        </div>

        {/* Simple session */}
        <section className="mx-auto mt-10 max-w-3xl rounded-3xl border border-slate-200 bg-slate-50 p-5 md:p-7">
          <h2 className="text-xl font-black text-slate-950">
            A simple practice session
          </h2>

          <ol className="mt-6 space-y-5">
            <PracticeStep
              number="1"
              title="Start from a model phrase"
              text="Use material from a course, audiobook, or dialogue."
            />

            <PracticeStep
              number="2"
              title="Echo or shadow"
              text="Repeat after the model, or speak together with it."
            />

            <PracticeStep
              number="3"
              title="Compare and repeat"
              text="Compare the model with your recording, then repeat the phrase to improve."
            />
          </ol>
        </section>

        {/* Practice time */}
        <section className="mx-auto mt-6 max-w-3xl rounded-3xl border border-green-200 bg-green-50 p-5 md:p-7">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <h2 className="text-xl font-black text-slate-950">
              Regular practice
            </h2>

            <div className="inline-flex w-fit rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-green-700">
              Start with 5–10 minutes
            </div>
          </div>

          <p className="mt-3 text-base font-semibold leading-8 text-slate-700">
            Pronunciation is physical and cognitive practice: listening, timing,
            motor skills, memory, and attention all work together. We recommend
            regular sessions, starting with 5–10 minutes and then adjusting the
            duration to what feels useful.
          </p>

          <p className="mt-4 text-base font-semibold leading-8 text-slate-700">
            A small number of phrases is usually enough for careful repetition.
            The aim is not to push hard in one session, but to return regularly
            to sounds, rhythm, intonation, and connected speech over time.
          </p>

          <p className="mt-4 text-base font-semibold leading-8 text-slate-700">
            One of the first changes may be perception. Learners may begin to
            hear not only separate words, but connected word chains joined by
            rhythm, intonation, and meaning.
          </p>

          <p className="mt-4 text-base font-semibold leading-8 text-slate-700">
            When these patterns become easier to notice, speech also becomes
            easier to process. Useful phrases and sentence patterns can then be
            absorbed and reused more naturally, instead of building everything
            word by word.
          </p>
        </section>

        {/* Deeper practice */}
        <details className="mx-auto mt-6 max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
          <summary className="cursor-pointer select-none text-lg font-black text-slate-950">
            A little deeper into pronunciation practice
          </summary>

          <div className="mt-6 border-t border-slate-100 pt-6">
            {/* Section 1 — Visual cues */}
            <section className="rounded-[28px] border border-slate-200 bg-slate-50/70 p-5 md:p-7">
              <div className="max-w-2xl">
                <div className="inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-slate-600">
                  Visual cues
                </div>

                <h2 className="mt-3 text-xl font-black text-slate-950">
                  Visual cues that help to follow the model
                </h2>

                <p className="mt-3 text-base font-semibold leading-8 text-slate-700">
                  The pitch curve shows where the voice rises or falls, and
                  which word carries the main stress.
                </p>
              </div>

              <ImagePreviewButton
                image={intonationImage}
                onClick={() => setOpenImage(intonationImage)}
                label="“Wouldn’t it be much” = one movement"
                text="These words form one intonation group. The pitch flows across them instead of restarting at each word."
              />

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <InfoCard title="Movement is not tied to word boundaries">
                  Pitch can continue across several words, or change inside one
                  word. In this example, the question rises toward the end:{' '}
                  <span className="font-black text-slate-950">there?</span>
                </InfoCard>

                <InfoCard title="Gaps can show breaks in voicing">
                  A gap means there is no measurable pitch. This can happen
                  during unvoiced sounds, but also during tiny pauses. If your
                  curve has many more gaps than the model, try a smoother
                  carry-through across words.
                </InfoCard>
              </div>
            </section>

            {/* Section 2 — Practice modes */}
            <section className="mt-10 rounded-[28px] border border-blue-100 bg-blue-50/60 p-5 md:p-7">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="inline-flex rounded-full border border-blue-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-blue-700">
                    Practice modes
                  </div>

                  <h3 className="mt-3 text-xl font-black text-slate-950">
                    Two ways to practise
                  </h3>
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">
                    Mode 1
                  </div>

                  <h4 className="mt-2 text-lg font-black text-slate-950">
                    Follow the intonation
                  </h4>

                  <p className="mt-2 text-base font-semibold leading-8 text-slate-700">
                    This is especially useful in intonation courses such as{' '}
                    <a
                      href="https://www.catchphraser.com/course/flyt-og-rytme"
                      className="font-black text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-900"
                    >
                      Flyt og rytme
                    </a>{' '}
                    or{' '}
                    <a
                      href="https://www.catchphraser.com/course/intonation-of-colloquial-english"
                      className="font-black text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-900"
                    >
                      Intonation of Colloquial English
                    </a>
                    . Listen to the model and notice how words are connected,
                    where the voice rises or falls, and which words carry the
                    main stress.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">
                    Mode 2
                  </div>

                  <h4 className="mt-2 text-lg font-black text-slate-950">
                    Use AI feedback as a pointer
                  </h4>

                  <p className="mt-2 text-base font-semibold leading-8 text-slate-700">
                    AI feedback may point to phonemes or words that need
                    attention. Do not treat it as a final judgement. Use it as a
                    suggestion for what to focus on next.
                  </p>

                  <ScoreLegend />
                </div>
              </div>

              <ImagePreviewButton
                image={aiImage}
                onClick={() => setOpenImage(aiImage)}
                label="Use feedback to choose one focus"
                text="Look at the marked phonemes or words, choose one thing to notice, and try the phrase again. The goal is not to fix everything at once."
                tone="blue"
              />

              {/* Spaced repetition */}
              <div className="mt-6 rounded-3xl border border-blue-100 bg-white p-5 shadow-sm md:p-6">
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="inline-flex rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-blue-700">
                      Spaced repetition
                    </div>

                    <h3 className="mt-3 text-xl font-black text-slate-950">
                      Sounds improve, fade, and improve again
                    </h3>
                  </div>

                  <div className="inline-flex w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-slate-600">
                    Normal process
                  </div>
                </div>

                <p className="mt-4 text-base font-semibold leading-8 text-slate-700">
                  A sound may go well today, and then partly return as a problem
                  later when attention moves elsewhere. This is normal for
                  cognitive and motor skills. Pronunciation skills become more
                  stable through repeated returns.
                </p>

                <div className="mt-5 overflow-hidden rounded-2xl border border-blue-100 bg-white">
                  <Suspense
                    fallback={
                      <div className="flex h-64 items-center justify-center text-sm font-semibold text-slate-500">
                        Loading learning curve…
                      </div>
                    }
                  >
                    <LearningProgressChart />
                  </Suspense>
                </div>

                <p className="mt-4 text-base font-semibold leading-8 text-slate-700">
                  Do not overwork red or yellow sounds. Notice the problem, try
                  again, and meet the same sound later in another phrase. Small
                  returns are part of the method, not a failure.
                </p>
              </div>
            </section>
          </div>
        </details>
      </div>

      {openImage && (
        <ImageOverlay image={openImage} onClose={() => setOpenImage(null)} />
      )}
    </>
  );
};

const PracticeStep = ({ number, title, text }) => {
  return (
    <li className="flex gap-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-sm font-black text-blue-700 shadow-sm">
        {number}
      </div>

      <div>
        <h3 className="text-base font-black text-slate-950">{title}</h3>

        <p className="mt-1 text-base font-semibold leading-7 text-slate-600">
          {text}
        </p>
      </div>
    </li>
  );
};

const InfoCard = ({ title, children }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-black text-slate-950">{title}</h3>

      <p className="mt-2 text-base font-semibold leading-8 text-slate-700">
        {children}
      </p>
    </div>
  );
};

const ScoreLegend = () => {
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-sm font-black text-slate-950">
        What the colours mean
      </div>

      <div className="mt-3 space-y-2 text-sm font-semibold leading-6 text-slate-700">
        <LegendRow
          colourClass="bg-green-500"
          label="Green"
          range="80–100"
          text="usually good enough to move on"
        />

        <LegendRow
          colourClass="bg-yellow-400"
          label="Yellow"
          range="40–80"
          text="notice it, but do not panic"
        />

        <LegendRow
          colourClass="bg-red-500"
          label="Red"
          range="below 40"
          text="try again, but do not overwork it"
        />
      </div>
    </div>
  );
};

const LegendRow = ({ colourClass, label, range, text }) => {
  return (
    <div className="flex gap-3">
      <span
        className={`mt-1.5 h-3 w-3 shrink-0 rounded-full ${colourClass}`}
        aria-hidden="true"
      />

      <div>
        <span className="font-black text-slate-950">{label}</span>{' '}
        <span className="font-black text-slate-700">({range})</span>: {text}.
      </div>
    </div>
  );
};

const ImagePreviewButton = ({ image, onClick, label, text, tone = 'slate' }) => {
  const isBlue = tone === 'blue';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group mt-6 block w-full overflow-hidden rounded-3xl border text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        isBlue
          ? 'border-blue-100 bg-white'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div className="overflow-hidden bg-white">
        <img
          src={image.src}
          alt={image.title}
          className="w-full transition duration-300 group-hover:scale-[1.015]"
        />
      </div>

      <div className={isBlue ? 'bg-white p-5 md:p-6' : 'p-5 md:p-6'}>
        <div className="text-base font-black text-slate-950">{label}</div>

        <p className="mt-2 text-base font-semibold leading-7 text-slate-700">
          {text}
        </p>

        <div className="mt-4 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
          Click to enlarge
        </div>
      </div>
    </button>
  );
};

const ImageOverlay = ({ image, onClose }) => {
  return (
    <div className="fixed inset-0 z-[250]">
      <button
        type="button"
        aria-label="Close image preview"
        onClick={onClose}
        className="absolute inset-0 bg-black/45"
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 md:p-6">
        <div
          className="relative flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3 md:px-5">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-black text-slate-950 md:text-xl">
                {image.title}
              </h2>

              {image.subtitle && (
                <p className="mt-1 truncate text-sm font-semibold text-slate-600 md:text-base">
                  {image.subtitle}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="overflow-auto bg-slate-50 p-3 md:p-5">
            <img
              src={image.src}
              alt={image.title}
              className="mx-auto h-auto max-h-[76vh] w-auto max-w-full rounded-2xl border border-slate-200 bg-white shadow-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowTo;
